import React, { useCallback, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import giftIcon from '../../assets/icons/gift.png';
import lockIcon from '../../assets/icons/lock.png';
import './GameSubNav.css';

/** Hold duration before Memory compose opens. */
const LONG_PRESS_MS = 450;

function padStat(n) {
  const v = Math.max(0, Math.floor(Number(n) || 0));
  return v < 10 ? `0${v}` : String(v);
}

/**
 * Page-specific bar above PublicBottomNav (Game / explore only).
 * Tabs: Explore · + · Collection
 * Short press + → Drop compose; long-press (~450ms) → Memory compose.
 *
 * Click owns short press (mobile-safe). Pointer handlers only drive the
 * long-press timer — no preventDefault on pointerdown / no touch capture
 * (those suppress click or cancel the press on iOS/Android).
 */
const GameSubNav = ({
  onAdd,
  onAddMemory,
  unopenedCount = 0,
  dropsFound = 0,
  dropsStillOut = 0,
}) => {
  const badgeLabel =
    unopenedCount > 9 ? '9+' : unopenedCount > 0 ? String(unopenedCount) : '';
  const timerRef = useRef(null);
  const longPressFiredRef = useRef(false);
  const [holding, setHolding] = useState(false);

  const clearPressTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startPress = useCallback(
    (e) => {
      if (e.button != null && e.button !== 0) return;
      longPressFiredRef.current = false;
      clearPressTimer();
      setHolding(true);
      timerRef.current = window.setTimeout(() => {
        longPressFiredRef.current = true;
        timerRef.current = null;
        setHolding(false);
        onAddMemory?.();
      }, LONG_PRESS_MS);
    },
    [clearPressTimer, onAddMemory]
  );

  const endPress = useCallback(() => {
    clearPressTimer();
    setHolding(false);
  }, [clearPressTimer]);

  const cancelPress = useCallback(() => {
    clearPressTimer();
    setHolding(false);
    // Keep longPressFiredRef so a trailing click after Memory open is ignored.
  }, [clearPressTimer]);

  const handleClick = useCallback(
    (e) => {
      if (longPressFiredRef.current) {
        e.preventDefault();
        longPressFiredRef.current = false;
        return;
      }
      onAdd?.();
    },
    [onAdd]
  );

  return (
    <nav className="game-subnav" aria-label="Explore actions">
      <div className="game-subnav__inner">
        <NavLink
          to="/game"
          end
          className={({ isActive }) =>
            `game-subnav__tab${isActive ? ' is-active' : ''}`
          }
        >
          Explore
        </NavLink>
        <button
          type="button"
          className={`game-subnav__add${holding ? ' is-holding' : ''}`}
          aria-label="Upload a drop. Press and hold for a memory."
          onPointerDown={startPress}
          onPointerUp={endPress}
          onPointerCancel={cancelPress}
          onPointerLeave={endPress}
          onContextMenu={(e) => e.preventDefault()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onAdd?.();
            }
          }}
          onClick={handleClick}
        >
          <span className="game-subnav__add-plus" aria-hidden="true">
            +
          </span>
          <span className="game-subnav__add-label" aria-hidden="true">
            Drop
          </span>
        </button>
        <NavLink
          to="/game/collection"
          className={({ isActive }) =>
            `game-subnav__tab game-subnav__tab--with-stats game-subnav__tab--collection${
              isActive ? ' is-active' : ''
            }`
          }
          aria-label={
            unopenedCount > 0
              ? `Collection, ${unopenedCount} unopened`
              : 'Collection'
          }
        >
          <span
            className="game-subnav__collection-stats"
            aria-label={`${dropsFound} collected, ${dropsStillOut} still out`}
          >
            <span className="game-subnav__collection-stat">
              <img src={giftIcon} alt="" className="game-subnav__collection-icon" />
              {padStat(dropsFound)}
            </span>
            <span className="game-subnav__collection-stat">
              <img src={lockIcon} alt="" className="game-subnav__collection-icon" />
              {padStat(dropsStillOut)}
            </span>
          </span>
          <span className="game-subnav__collection-label">
            Collection
            {badgeLabel ? (
              <span className="game-subnav__badge" aria-hidden="true">
                {badgeLabel}
              </span>
            ) : null}
          </span>
        </NavLink>
      </div>
    </nav>
  );
};

export default GameSubNav;
